/**
 * <kk-header> — behaviour for sections/kk-header.liquid
 *
 * - Desktop dropdowns: chevron buttons toggle [data-open] on their <li> (hover opens them via CSS too).
 *   Esc or a click outside closes them.
 * - Mobile drawer: native <dialog> (focus trap + Esc for free). Parent items slide in their submenu
 *   panel; everything behind an open panel is made inert so keyboard focus stays in the panel.
 * - Search: <dialog> with Shopify predictive search (/search/suggest.json), results built with
 *   textContent (no HTML injection), "View all results" link to the search page.
 * - Sticky "scroll-up" mode hides the bar while scrolling down.
 * - Sticky modes set [data-stuck] once the page has scrolled, so the "colors when sticky" settings can apply.
 * - Publishes the bar height as --kk-header-height on <html> for other components.
 */
const SEARCH_DELAY = 250;
const SEARCH_MIN_CHARS = 2;

class KKHeader extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.bar = this.querySelector('.kk-header__bar');
    this.drawer = /** @type {HTMLDialogElement | null} */ (this.querySelector('.kk-header__drawer'));
    this.search = /** @type {HTMLDialogElement | null} */ (this.querySelector('.kk-header__search'));
    this.searchInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-kk-search-input]'));
    this.searchResults = this.querySelector('[data-kk-search-results]');

    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    this.addEventListener('keydown', (event) => this.#onKeydown(event), { signal });
    document.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Node) || !this.contains(event.target)) this.#closeDropdowns();
      },
      { signal }
    );

    this.drawer?.addEventListener('close', () => this.#onDrawerClose(), { signal });

    if (this.search) {
      this.search.addEventListener('close', () => this.#unlockScroll(), { signal });
      // Click on the backdrop (the dialog element itself, outside its content) closes it.
      this.search.addEventListener('click', (event) => event.target === this.search && this.search.close(), { signal });
      this.searchInput?.addEventListener('input', () => this.#queueSearch(), { signal });
    }

    if (this.dataset.sticky === 'scroll-up') this.#setupScrollUp(signal);
    if (this.dataset.sticky !== 'none') this.#setupStuck(signal);

    if (this.bar) {
      this.resizeObserver = new ResizeObserver(([entry]) => {
        document.documentElement.style.setProperty('--kk-header-height', `${Math.round(entry.contentRect.height)}px`);
      });
      this.resizeObserver.observe(this.bar);
    }
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.searchController?.abort();
    this.resizeObserver?.disconnect();
    clearTimeout(this.searchTimer);
    this.#unlockScroll();
  }

  // ---------------------------------------------------------------- clicks & keys

  #onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const control = target.closest(
      '[data-kk-drawer-open], [data-kk-drawer-close], [data-kk-submenu-open], [data-kk-submenu-back], [data-kk-dropdown-toggle], [data-kk-search-open], [data-kk-search-close]'
    );
    if (!control) return;

    if (control.hasAttribute('data-kk-drawer-open')) this.#openDrawer(control);
    else if (control.hasAttribute('data-kk-drawer-close')) this.drawer?.close();
    else if (control.hasAttribute('data-kk-submenu-open')) this.#openPanel(control);
    else if (control.hasAttribute('data-kk-submenu-back')) this.#closePanel(control.closest('[data-kk-submenu]'), true);
    else if (control.hasAttribute('data-kk-dropdown-toggle')) this.#toggleDropdown(control);
    else if (control.hasAttribute('data-kk-search-open')) this.#openSearch();
    else if (control.hasAttribute('data-kk-search-close')) this.search?.close();
  }

  #onKeydown(event) {
    if (event.key !== 'Escape') return;
    const openItem = event.target instanceof Element ? event.target.closest('.kk-header__item[data-open]') : null;
    if (openItem) {
      this.#closeDropdowns();
      /** @type {HTMLElement | null} */ (openItem.querySelector(':scope > [data-kk-dropdown-toggle]'))?.focus();
    }
  }

  // ---------------------------------------------------------------- desktop dropdowns

  #toggleDropdown(button) {
    const item = button.closest('.kk-header__item');
    if (!item) return;
    const open = !item.hasAttribute('data-open');

    // Close siblings (and their children) at the same level.
    for (const sibling of item.parentElement?.children ?? []) {
      if (sibling !== item) this.#closeItem(sibling);
    }

    if (open) {
      item.setAttribute('data-open', '');
      button.setAttribute('aria-expanded', 'true');
    } else {
      this.#closeItem(item);
    }
  }

  #closeItem(item) {
    item.removeAttribute('data-open');
    item.querySelector(':scope > [data-kk-dropdown-toggle]')?.setAttribute('aria-expanded', 'false');
    item.querySelectorAll('.kk-header__item[data-open]').forEach((child) => this.#closeItem(child));
  }

  #closeDropdowns() {
    this.querySelectorAll('.kk-header__list--level-1 > .kk-header__item[data-open]').forEach((item) => this.#closeItem(item));
  }

  // ---------------------------------------------------------------- mobile drawer

  #openDrawer(button) {
    if (!this.drawer) return;
    this.drawer.showModal();
    button.setAttribute('aria-expanded', 'true');
    this.#lockScroll();
  }

  #onDrawerClose() {
    this.querySelectorAll('[data-kk-submenu][data-open]').forEach((panel) => this.#closePanel(panel, false));
    this.querySelector('[data-kk-drawer-open]')?.setAttribute('aria-expanded', 'false');
    this.#unlockScroll();
  }

  #openPanel(button) {
    const panel = document.getElementById(button.getAttribute('aria-controls') || '');
    const list = button.closest('ul');
    if (!panel || !list) return;

    // Make everything behind the panel unreachable by keyboard / screen readers.
    const behind = [
      ...[...list.children].filter((li) => li !== button.parentElement),
      ...[...(list.parentElement?.children ?? [])].filter((el) => el !== list),
      button,
    ];
    behind.forEach((el) => (el.inert = true));
    panel._kkBehind = behind;

    panel.inert = false;
    panel.setAttribute('data-open', '');
    button.setAttribute('aria-expanded', 'true');
    // Read layout so the panel's new (visible) style applies before focusing into it.
    void panel.offsetWidth;
    /** @type {HTMLElement | null} */ (panel.querySelector('[data-kk-submenu-back]'))?.focus({ preventScroll: true });
  }

  #closePanel(panel, restoreFocus) {
    if (!panel) return;
    panel.querySelectorAll('[data-kk-submenu][data-open]').forEach((child) => this.#closePanel(child, false));

    panel.removeAttribute('data-open');
    panel.inert = true;
    (panel._kkBehind || []).forEach((el) => (el.inert = false));
    panel._kkBehind = null;

    const opener = this.querySelector(`[data-kk-submenu-open][aria-controls="${CSS.escape(panel.id)}"]`);
    opener?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) /** @type {HTMLElement | null} */ (opener)?.focus();
  }

  // ---------------------------------------------------------------- search

  #openSearch() {
    if (!this.search) return;
    this.search.showModal();
    this.#lockScroll();
    this.searchInput?.focus();
    this.searchInput?.select();
  }

  #queueSearch() {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.#runSearch(), SEARCH_DELAY);
  }

  async #runSearch() {
    const query = this.searchInput?.value.trim() ?? '';
    this.searchController?.abort();

    if (query.length < SEARCH_MIN_CHARS) {
      this.searchResults?.replaceChildren();
      return;
    }

    this.searchController = new AbortController();
    const url = new URL(`${this.dataset.predictiveUrl || '/search/suggest'}.json`, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product,collection,page');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[options][unavailable_products]', 'last');

    try {
      const response = await fetch(url, { signal: this.searchController.signal });
      if (!response.ok) throw new Error(String(response.status));
      const data = await response.json();
      this.#renderResults(data?.resources?.results ?? {}, query);
    } catch (error) {
      if (error?.name !== 'AbortError') this.#renderResults({}, query);
    }
  }

  #renderResults(results, query) {
    if (!this.searchResults) return;
    const fragment = document.createDocumentFragment();
    const products = results.products ?? [];
    const others = [...(results.collections ?? []), ...(results.pages ?? [])];

    if (products.length) fragment.append(this.#group('Products', products, true));
    if (others.length) fragment.append(this.#group('Pages & collections', others, false));

    const searchUrl = new URL(this.dataset.searchUrl || '/search', window.location.origin);
    searchUrl.searchParams.set('q', query);
    searchUrl.searchParams.set('options[prefix]', 'last');

    if (products.length || others.length) {
      const all = document.createElement('a');
      all.className = 'kk-header-search__all';
      all.href = searchUrl.toString();
      all.textContent = `View all results for “${query}”`;
      fragment.append(all);
    } else {
      const empty = document.createElement('p');
      empty.className = 'kk-header-search__empty';
      empty.textContent = `No results for “${query}”`;
      fragment.append(empty);
    }

    this.searchResults.replaceChildren(fragment);
  }

  #group(title, items, withMedia) {
    const wrapper = document.createElement('div');
    const heading = document.createElement('p');
    heading.className = 'kk-header-search__group-title';
    heading.textContent = title;

    const list = document.createElement('ul');
    list.className = 'kk-header-search__list';
    list.setAttribute('role', 'list');

    for (const item of items) {
      const li = document.createElement('li');
      const link = document.createElement('a');
      link.className = 'kk-header-search__item';
      link.href = item.url;

      if (withMedia) {
        const src = item.featured_image?.url || item.image;
        if (src) {
          const img = document.createElement('img');
          const imageUrl = new URL(src, window.location.origin);
          imageUrl.searchParams.set('width', '120');
          img.src = imageUrl.toString();
          img.alt = '';
          img.width = 48;
          img.height = 48;
          img.loading = 'lazy';
          img.className = 'kk-header-search__thumb';
          link.append(img);
        } else {
          const placeholder = document.createElement('span');
          placeholder.className = 'kk-header-search__thumb';
          link.append(placeholder);
        }
      }

      const text = document.createElement('span');
      const name = document.createElement('span');
      name.className = 'kk-header-search__name';
      name.textContent = item.title;
      text.append(name);

      if (withMedia && item.price) {
        const price = document.createElement('span');
        price.className = 'kk-header-search__price';
        price.textContent = this.#formatPrice(item.price);
        text.append(price);
      }

      link.append(text);
      li.append(link);
      list.append(li);
    }

    wrapper.append(heading, list);
    return wrapper;
  }

  #formatPrice(amount) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'INR',
      }).format(Number(amount));
    } catch {
      return amount;
    }
  }

  // ---------------------------------------------------------------- sticky + scroll lock

  /** Horizon scrolls the page inside .page-wrapper on desktop (>= 990px) and in the window below that. */
  #scrollY() {
    const wrapper = document.querySelector('.page-wrapper');
    return Math.max(window.scrollY, wrapper ? wrapper.scrollTop : 0);
  }

  /** True for scroll events of the window/document or of the page wrapper (not of menus, drawers, lists…). */
  #isPageScroll(event) {
    const target = event.target;
    return target === document || (target instanceof Element && target.classList.contains('page-wrapper'));
  }

  #setupScrollUp(signal) {
    let lastY = this.#scrollY();
    let frame = 0;

    // scroll events do not bubble from elements, so listen in the capture phase
    document.addEventListener(
      'scroll',
      (event) => {
        if (frame || !this.#isPageScroll(event)) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          const y = this.#scrollY();
          const height = this.bar?.offsetHeight ?? 0;
          const hide = y > lastY && y > height && !this.drawer?.open && !this.search?.open && !this.querySelector('[data-open]');
          this.toggleAttribute('data-hidden', hide);
          lastY = y;
        });
      },
      { passive: true, capture: true, signal }
    );
  }

  #setupStuck(signal) {
    let frame = 0;
    const update = () => {
      frame = 0;
      this.toggleAttribute('data-stuck', this.#scrollY() > 8);
    };

    document.addEventListener(
      'scroll',
      (event) => {
        if (!frame && this.#isPageScroll(event)) frame = requestAnimationFrame(update);
      },
      { passive: true, capture: true, signal }
    );
    update();
  }

  #lockScroll() {
    document.documentElement.style.overflow = 'hidden';
  }

  #unlockScroll() {
    if (this.drawer?.open || this.search?.open) return;
    document.documentElement.style.overflow = '';
  }
}

if (!customElements.get('kk-header')) {
  customElements.define('kk-header', KKHeader);
}
